import type { RuteroPassengerFieldRef } from '@bandido/shared/types';

import type { BookingMoment } from './bookingMoment';

/**
 * El CTA primario de la barra de decisión: uno por momento, o ninguno.
 *
 * `null` no es "botón gris": es que en este momento no hay nada que decidir
 * desde acá (la reserva está cerrada, o la oferta dejó de ser reservable y el
 * cuerpo ya explica por qué con su aviso).
 */
export type BookingPrimaryAction = 'hold' | 'confirm' | null;

/**
 * Por qué el CTA está deshabilitado. Es un vocabulario cerrado a propósito:
 * cada motivo tiene su texto y se dibuja PEGADO al botón — un botón
 * deshabilitado sin motivo es un callejón sin salida.
 */
export type BookingBlockedReason =
  /** Falta elegir el caso comercial que agrupa la reserva. */
  | 'case'
  /** Faltan datos que la oferta declara obligatorios. */
  | 'passengers'
  /** Faltan campos declarados concretos y se pueden nombrar. */
  | 'passengerFields'
  /** El pedido exige ida y vuelta y la oferta no demuestra ambos tramos. */
  | 'incompleteTrip'
  /**
   * El conector exige para reservar un dato que ESTE pedido no puede tener
   * (el vuelo de regreso de un solo ida). No hay nada que completar: el hub
   * rechaza siempre, y su rechazo vuelve a nombrar el mismo campo. Se bloquea
   * nombrándolo, en vez de dejar mandar un pedido que se sabe imposible.
   */
  | 'shapeContradiction'
  /**
   * Un dato cargado supera el largo máximo que acepta el proveedor (hoy solo
   * puede pasarle al multilínea: los demás controles recortan en el input).
   * No se recorta a propósito —el hub responde 400 en vez de truncar—, así
   * que la salida es acortarlo, y el motivo lo nombra.
   */
  | 'fieldTooLong'
  /** El operador rechazó la selección de plazas/asientos, o está incompleta. */
  | 'options'
  | 'luggageQuote'
  /** Cambió un horario: el precio vigente hay que volver a pedirlo. */
  | 'requote'
  /** Se está re-cotizando con el horario nuevo. */
  | 'requoting'
  /** Todavía no llegaron las opciones que la oferta deja elegir. */
  | 'loadingOptions'
  /** El operador pregunta algo obligatorio y sin respuesta el confirm falla. */
  | 'questions'
  /**
   * El operador publicó más preguntas obligatorias que las respuestas que el
   * confirm admite: no existe un pedido válido que las lleve a todas. Se
   * bloquea diciéndolo, en vez de mandar algo que se sabe que falla.
   */
  | 'answersOverflow'
  /**
   * El operador rechazó ESTE pedido (`provider_policy`, o el rechazo limpio
   * del proveedor). Mandarlo igual no cambia nada —lo dice el contrato del
   * hub—, así que el CTA se bloquea hasta que el pedido cambie. La oferta
   * NO murió: el formulario sigue ahí porque corregir es el camino.
   */
  | 'requestRefused'
  /**
   * Defensa post-hold para una reserva histórica o un contrato que publicó
   * tarde datos de pasajero validados al confirmar. Los flujos nuevos los
   * bloquean antes del hold; si llegan acá, se nombran como irreparables.
   */
  | 'confirmPassengers';

/**
 * Un bloqueo, con los campos exactos cuando el motivo nombra datos.
 *
 * Los campos viajan como refs del contrato (`contact.phone`) y no como texto:
 * el rótulo es de la UI. Van ACÁ y no en un prop aparte de la barra para que la
 * lista que se lee al lado del botón salga de la misma expresión que lo
 * deshabilita — si fueran dos, el botón podría quedar gris por un campo que la
 * pantalla no nombra.
 */
export interface BookingBlockedIssue {
  reason: BookingBlockedReason;
  fields?: RuteroPassengerFieldRef[];
}

export interface BookingDecisionInput {
  moment: BookingMoment;
  hold: {
    /**
     * La oferta todavía se puede holdear: sale de la búsqueda vigente, no
     * venció y no la invalidó un rechazo. En falso no hay CTA — el aviso del
     * cuerpo es la salida (volver a buscar), no este botón.
     */
    offered: boolean;
    caseSelected: boolean;
    passengersValid: boolean;
    tripComplete: boolean;
    /** Datos exactos del pasajero que deben viajar en el hold. */
    missingPassengerFields: RuteroPassengerFieldRef[];
    /**
     * Campos cuyo valor excede el tope que acepta el borde
     * (`passengerFieldsOverLimit`): mandarlos es un 400 seguro, así que
     * bloquean nombrándose. Vacío ⇒ nada que acortar.
     */
    fieldsOverLimit: RuteroPassengerFieldRef[];
    /** Lo que el conector exige para reservar y este pedido no puede tener. */
    shapeContradictions: RuteroPassengerFieldRef[];
    optionsValid: boolean;
    luggageQuoteBlocked?: boolean;
    scheduleChanged: boolean;
    requoting: boolean;
    optionsLoading: boolean;
    /**
     * El operador rechazó este MISMO pedido y todavía no se corrigió nada.
     * Sale de comparar la identidad del pedido actual contra la del rechazado
     * —la misma identidad que gobierna la idempotency key—, así que en cuanto
     * el asesor cambia algo el bloqueo se levanta solo.
     */
    requestRefused: boolean;
  };
  confirm: {
    /** El estado de la reserva admite confirmar (contrato + operación viva). */
    available: boolean;
    questionsAnswered: boolean;
    /**
     * Las obligatorias no entran en un solo confirm (más que
     * `answers.maxItems`): no hay payload válido posible.
     */
    mandatoryAnswersOverflow: boolean;
    /**
     * Lo que la oferta declaró con `validatedAt: confirm` y todavía no está
     * cargado en los pasajeros del REGISTRO. Vacío ⇒ nada que completar.
     */
    missingPassengerFields: RuteroPassengerFieldRef[];
  };
}

export interface BookingDecision {
  primary: BookingPrimaryAction;
  /** Vacío ⇒ el CTA está habilitado. Es la MISMA expresión que `disabled`. */
  blocked: BookingBlockedIssue[];
  disabled: boolean;
}

/**
 * Qué se puede decidir en este momento, y si no se puede, por qué.
 *
 * `disabled` se deriva de `blocked` a propósito: si fueran dos expresiones
 * podría quedar un botón bloqueado sin explicación (o al revés, un motivo
 * escrito al lado de un botón habilitado).
 */
export function resolveBookingDecision({
  moment,
  hold,
  confirm,
}: BookingDecisionInput): BookingDecision {
  const blocked: BookingBlockedIssue[] = [];
  let primary: BookingPrimaryAction = null;

  if (moment === 'review' && hold.offered) {
    primary = 'hold';
    if (!hold.caseSelected) blocked.push({ reason: 'case' });
    if (!hold.passengersValid) {
      blocked.push({
        reason: hold.missingPassengerFields.length
          ? 'passengerFields'
          : 'passengers',
        ...(hold.missingPassengerFields.length
          ? { fields: hold.missingPassengerFields }
          : {}),
      });
    }
    if (!hold.tripComplete) blocked.push({ reason: 'incompleteTrip' });
    if (hold.fieldsOverLimit.length > 0) {
      blocked.push({ reason: 'fieldTooLong', fields: hold.fieldsOverLimit });
    }
    if (hold.shapeContradictions.length > 0) {
      blocked.push({
        reason: 'shapeContradiction',
        fields: hold.shapeContradictions,
      });
    }
    if (!hold.optionsValid) blocked.push({ reason: hold.luggageQuoteBlocked ? 'luggageQuote' : 'options' });
    if (hold.scheduleChanged) blocked.push({ reason: 'requote' });
    if (hold.requoting) blocked.push({ reason: 'requoting' });
    if (hold.optionsLoading) blocked.push({ reason: 'loadingOptions' });
    if (hold.requestRefused) blocked.push({ reason: 'requestRefused' });
  }

  if (moment === 'reserved' && confirm.available) {
    primary = 'confirm';
    if (!confirm.questionsAnswered) blocked.push({ reason: 'questions' });
    if (confirm.mandatoryAnswersOverflow) {
      blocked.push({ reason: 'answersOverflow' });
    }
    if (confirm.missingPassengerFields.length > 0) {
      blocked.push({
        reason: 'confirmPassengers',
        fields: confirm.missingPassengerFields,
      });
    }
  }

  return { primary, blocked, disabled: blocked.length > 0 };
}

/**
 * Las condiciones que cambian la decisión y por eso viven PEGADAS al botón, no
 * en un bloque tres pantallas arriba.
 *
 * Salen de campos que el hub publica para cualquier vertical (no hay rama por
 * vertical acá). Dos ausencias a propósito:
 *
 * - "No reembolsable" no se deduce de que no venga `freeCancellationUntil`:
 *   ausencia no es negación, y la política real vive en un endpoint aparte
 *   (`cancellation-policy`).
 * - Post-hold no hay flags: el matiz que importa —que el operador todavía no
 *   aceptó la reserva— ya lo dice el badge de estado, que rotula el
 *   `providerStatus` on-request en vez de cantar "confirmado".
 */
export type BookingFlagKind =
  | 'instantConfirmation'
  | 'onRequestConfirmation'
  | 'freeCancellation';

export interface BookingFlag {
  kind: BookingFlagKind;
  tone: 'ok' | 'warn';
  /** Dato crudo del proveedor que completa el texto (lo formatea la UI). */
  value?: string;
}

export function resolveBookingFlags(
  offer: { instantConfirmation?: unknown; freeCancellationUntil?: unknown } | null,
): BookingFlag[] {
  if (!offer) return [];
  const flags: BookingFlag[] = [];

  // Tri-estado: solo se afirma lo que el proveedor afirmó.
  if (typeof offer.instantConfirmation === 'boolean') {
    flags.push(
      offer.instantConfirmation
        ? { kind: 'instantConfirmation', tone: 'ok' }
        : { kind: 'onRequestConfirmation', tone: 'warn' },
    );
  }

  if (
    typeof offer.freeCancellationUntil === 'string' &&
    offer.freeCancellationUntil.trim()
  ) {
    flags.push({
      kind: 'freeCancellation',
      tone: 'ok',
      value: offer.freeCancellationUntil,
    });
  }

  return flags;
}

/**
 * El ancla entre el CTA y su motivo. Es UNA constante para las dos puntas —el
 * `aria-describedby` del botón y el `id` del párrafo— porque un
 * `aria-describedby` que apunta a un id que no existe no anuncia nada, y en
 * pantalla no se nota.
 */
export const BOOKING_BLOCKED_REASONS_ID = 'booking-decision-blocked';

/** Cada motivo con su texto; los que nombran datos reciben los campos. */
const REASON_KEY: Record<BookingBlockedReason, string> = {
  case: 'travelServices.booking.blocked.case',
  passengers: 'travelServices.booking.blocked.passengers',
  passengerFields: 'travelServices.booking.blocked.passengerFields',
  incompleteTrip: 'travelServices.booking.blocked.incompleteTrip',
  fieldTooLong: 'travelServices.booking.blocked.fieldTooLong',
  shapeContradiction: 'travelServices.booking.blocked.shapeContradiction',
  options: 'travelServices.booking.blocked.options',
  luggageQuote: 'travelServices.booking.blocked.luggageQuote',
  requote: 'travelServices.booking.blocked.requote',
  requoting: 'travelServices.booking.blocked.requoting',
  loadingOptions: 'travelServices.booking.blocked.loadingOptions',
  questions: 'travelServices.booking.blocked.questions',
  answersOverflow: 'travelServices.booking.blocked.answersOverflow',
  confirmPassengers: 'travelServices.booking.blocked.confirmPassengers',
  requestRefused: 'travelServices.booking.blocked.requestRefused',
};

/** Traduce; el segundo argumento son las interpolaciones (i18next). */
export type TranslateBlocked = (
  key: string,
  values?: Record<string, string>,
) => string;

/**
 * El texto que se lee al lado del botón, y el MISMO que anuncia el lector de
 * pantalla: el motivo y los campos que lo explican salen del mismo issue, así
 * que un motivo que nombra datos nunca queda sin nombrarlos.
 */
export function blockedReasonsText(
  blocked: BookingBlockedIssue[],
  t: TranslateBlocked,
  fieldLabels: (fields: RuteroPassengerFieldRef[]) => string[],
): string {
  return blocked
    .map(({ reason, fields }) => {
      const names = fields ? fieldLabels(fields).join(', ') : '';
      return t(REASON_KEY[reason], { fields: names });
    })
    .join(' · ');
}

/**
 * Los atributos del CTA cuando está bloqueado.
 *
 * `disabled` es SIEMPRE false a propósito: un `<button disabled>` sale del
 * orden de tabulación y los lectores de pantalla no lo anuncian, así que quien
 * navega con teclado se topa con un botón que no existe y nunca escucha por
 * qué. Con `aria-disabled` el botón se sigue enfocando, anuncia su estado y
 * lee su motivo — y el click no hace nada (lo corta quien lo dibuja, con esta
 * misma expresión).
 */
export interface BookingCtaA11y {
  disabled: false;
  'aria-disabled': true | undefined;
  'aria-describedby': string | undefined;
}

export function bookingCtaA11y(blocked: BookingBlockedIssue[]): BookingCtaA11y {
  const isBlocked = blocked.length > 0;
  return {
    disabled: false,
    'aria-disabled': isBlocked || undefined,
    'aria-describedby': isBlocked ? BOOKING_BLOCKED_REASONS_ID : undefined,
  };
}
